import React from "react";
import LoadingSpinner from "./LoadingSpinner";
import { PeriodCount, StatData } from "../List";
import { useTranslation } from "react-i18next";
import { Card, Row, Col, Table } from "react-bootstrap";
import StatBadge from "./StatBadge";

const StatisticsTab = ({ data }: { data: StatData | null }) => {
  const { t } = useTranslation();

  if (!data) return <LoadingSpinner />;

  const periods: { key: "week" | "month" | "year"; label: string }[] = [
    { key: "week", label: t("statistics.thisWeek") },
    { key: "month", label: t("statistics.thisMonth") },
    { key: "year", label: t("statistics.thisYear") },
  ];

  const metrics: {
    key: keyof StatData;
    label: string;
    icon: string;
    color: string;
  }[] = [
    {
      key: "coaches",
      label: t("statistics.coaches"),
      icon: "ph-duotone ph-user-circle",
      color: "primary",
    },
    {
      key: "companies",
      label: t("statistics.companies"),
      icon: "ph-duotone ph-buildings",
      color: "success",
    },
    {
      key: "jobs",
      label: t("statistics.jobs"),
      icon: "ph-duotone ph-briefcase",
      color: "warning",
    },
    {
      key: "matches",
      label: t("statistics.matches"),
      icon: "ph-duotone ph-handshake",
      color: "info",
    },
  ];

  return (
    <Row className="g-4">
      {/* Summary cards */}
      {metrics.map((m) => {
        const d = data[m.key] as PeriodCount | undefined;
        return (
          <Col md={6} xl={3} key={m.key}>
            <Card className="text-center h-100">
              <Card.Body className="py-4">
                <i className={`${m.icon} fs-2 text-${m.color} mb-3 d-block`} />
                <h6 className="mb-3">{m.label}</h6>
                <div className="d-flex justify-content-around">
                  {periods.map((p) => (
                    <StatBadge
                      key={p.key}
                      value={d?.[p.key] ?? 0}
                      label={p.label}
                    />
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        );
      })}

      {/* Subscriptions */}
      {data.subscriptions && (
        <Col xl={12}>
          <Card>
            <Card.Header>
              <h6 className="mb-0">{t("statistics.subscriptions")}</h6>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                {periods.map((p) => {
                  const sub = data.subscriptions![p.key];
                  return (
                    <Col md={4} key={p.key}>
                      <div className="border rounded p-3">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">{p.label}</h6>
                          <span className="badge bg-primary rounded-pill">
                            {sub?.total ?? 0}
                          </span>
                        </div>
                        <Table borderless size="sm" className="mb-0">
                          <tbody>
                            {(sub?.byPackage ?? []).map((pkg) => (
                              <tr key={pkg.packageId}>
                                <td className="text-muted small">
                                  {pkg.packageName}
                                </td>
                                <td className="text-end fw-semibold">
                                  {pkg.count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default StatisticsTab;
